import ENDPOINTS from '@/config/apiEndpoints';
import { NewsInterface } from '@/interfaces';
import ApiService from '@/services/ApiService';
import { logError, logInfo } from '@/utils/logger';
const CACHE_TTL_MS = 5 * 60 * 1000;
const sleep = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};
class NewsService {
  private static instance: NewsService;
  private newsCache: {
    data: NewsInterface[];
    timestamp: number;
  } | null = null;
  private constructor() {}
  public static getInstance(): NewsService {
    if (!NewsService.instance) {
      NewsService.instance = new NewsService();
    }
    return NewsService.instance;
  }
  private async makeRequestWithRetry(
    endpoint: string,
    maxRetries: number = 3,
    initialBackoff: number = 1000,
  ): Promise<any> {
    let retries = 0;
    let lastError: Error | null = null;
    while (retries <= maxRetries) {
      try {
        return await ApiService.get(endpoint);
      } catch (error) {
        lastError = error as Error;
        const shouldRetry = this.isRetryableError(error);
        if (shouldRetry) {
          const backoffTime = initialBackoff * Math.pow(2, retries);
          logInfo(
            `API request failed. Retrying in ${backoffTime}ms (retry ${
              retries + 1
            }/${maxRetries + 1})`,
            'NewsService.makeRequestWithRetry',
          );
          await sleep(backoffTime);
          retries++;
        } else {
          throw error;
        }
      }
    }
    throw lastError || new Error('Failed after maximum retries');
  }
  private isRetryableError(error: any): boolean {
    if (!error.response) {
      return true;
    }
    return (
      error.response.status === 429 ||
      (error.response.status >= 500 && error.response.status < 600)
    );
  }
  public async fetchNews(): Promise<NewsInterface[]> {
    logInfo('Fetching news', 'NewsService.fetchNews');
    try {
      const now = Date.now();
      if (this.newsCache && now - this.newsCache.timestamp < CACHE_TTL_MS) {
        logInfo('Returning cached news data', 'NewsService.fetchNews');
        return this.newsCache.data;
      }
      logInfo('Fetching fresh news data from API', 'NewsService.fetchNews');
      const response = await this.makeRequestWithRetry(ENDPOINTS.NEWS);
      const newsData: NewsInterface[] = Array.isArray(response) ? response : [];
      this.newsCache = {
        data: newsData,
        timestamp: now,
      };
      logInfo('Fetched news data', 'NewsService.fetchNews', newsData);
      return newsData;
    } catch (error) {
      logError(error, 'NewsService.fetchNews');
      if (this.newsCache) {
        logInfo(
          'Returning stale cached data due to fetch error',
          'NewsService.fetchNews',
        );
        return this.newsCache.data;
      }
      throw new Error('Failed to fetch news');
    }
  }
}
export default NewsService.getInstance();
